<template>
 <MarketingShell>
 <div class="tm-integrated tm-help">
  <div class="help-page" :class="{ 'dark-mode': isDark }">
    <!-- Maintenance Banner -->
    <div class="maintenance-banner" v-if="showMaintenanceBanner">
      <div class="maintenance-content">
        <div class="maintenance-icon">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
          </svg>
        </div>
        <div class="maintenance-text">
          <h3>Help Center Under Maintenance</h3>
          <p>We're currently updating our documentation to serve you better. Please check back soon.</p>
        </div>
        <button class="maintenance-close" @click="showMaintenanceBanner = false">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        </button>
      </div>
    </div>

    <!-- Top Navigation Bar -->
    <header class="help-header">
      <div class="help-header-left">
        <button class="mobile-menu-btn" @click="sidebarOpen = !sidebarOpen">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M3 12h18M3 6h18M3 18h18"/>
          </svg>
        </button>
        
        
        <span class="help-brand-section">Docs</span>
      </div>
      <div class="help-header-right">
        <div class="help-search-wrapper" :class="{ focused: searchFocused }">
          <svg class="help-search-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <input
            ref="searchInput"
            type="text"
            v-model="searchQuery"
            @focus="searchFocused = true"
            @blur="onSearchBlur"
            @input="performSearch"
            @keydown.escape="clearSearch"
            @keydown.down.prevent="highlightNext"
            @keydown.up.prevent="highlightPrev"
            @keydown.enter.prevent="selectHighlighted"
            placeholder="Search docs..."
            class="help-search-input"
          >
          <kbd class="help-search-kbd" v-show="!searchFocused && !searchQuery">⌘K</kbd>
        </div>
      </div>
      <!-- Search Results Dropdown -->
      <div class="help-search-results" v-show="searchResults.length > 0 && searchFocused">
        <div
          v-for="(result, idx) in searchResults"
          :key="result.slug"
          class="help-search-result-item"
          :class="{ highlighted: idx === highlightedIndex }"
          @mousedown.prevent="navigateTo(result.slug)"
        >
          <div class="help-search-result-title">{{ result.title }}</div>
          <div class="help-search-result-desc">{{ result.description || result.content.substring(0, 100) }}</div>
        </div>
      </div>
    </header>

    <div class="help-layout">
      <!-- Sidebar -->
      <aside class="help-sidebar" :class="{ open: sidebarOpen }">
        <nav class="help-nav">
          <div v-for="group in navigation" :key="group.group" class="help-nav-group">
            <div class="help-nav-group-title">{{ group.group }}</div>
            <ul class="help-nav-list">
              <li v-for="item in group.items" :key="item.link">
                <router-link
                  v-if="!showMaintenanceBanner"
                  :to="'/help/' + item.link"
                  class="help-nav-link"
                  :class="{ active: currentSlug === item.link }"
                  @click.native="sidebarOpen = false"
                >
                  {{ item.text }}
                </router-link>
                <span
                  v-else
                  class="help-nav-link disabled"
                  :title="'Documentation unavailable during maintenance'"
                >
                  {{ item.text }}
                </span>
              </li>
            </ul>
          </div>
        </nav>
      </aside>

      <!-- Sidebar Overlay for Mobile -->
      <div class="help-sidebar-overlay" v-show="sidebarOpen" @click="sidebarOpen = false"></div>

      <!-- Main Content -->
      <div class="help-main">
        <!-- Maintenance Overlay -->
        <div class="maintenance-overlay" v-if="showMaintenanceBanner">
          <div class="maintenance-overlay-content">
            <div class="maintenance-overlay-icon">
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M12 2v4m0 12v4M4.93 4.93l2.83 2.83m8.48 8.48l2.83 2.83M2 12h4m12 0h4M4.93 19.07l2.83-2.83m8.48-8.48l2.83-2.83"/>
              </svg>
            </div>
            <h2>Help Center Under Maintenance</h2>
            <p>We're currently updating our documentation to provide you with better and more comprehensive help content.</p>
            <p class="maintenance-eta">Expected completion: Soon</p>
            <div class="maintenance-actions">
              <a href="/contact-support" class="maintenance-btn primary">Contact Support</a>
            </div>
          </div>
        </div>

        <article class="help-content" v-else-if="renderedContent" v-html="renderedContent"></article>
        <article class="help-content help-not-found" v-else>
          <h1>Page Not Found</h1>
          <p>The documentation page you're looking for doesn't exist.</p>
          <router-link to="/help/introduction" class="help-back-link">← Back to Introduction</router-link>
        </article>

        <!-- Page Navigation (Prev/Next) -->
        <div class="help-page-nav" v-if="renderedContent">
          <router-link v-if="prevPage" :to="'/help/' + prevPage.link" class="help-page-nav-link prev">
            <span class="help-page-nav-label">Previous</span>
            <span class="help-page-nav-title">{{ prevPage.text }}</span>
          </router-link>
          <div v-else></div>
          <router-link v-if="nextPage" :to="'/help/' + nextPage.link" class="help-page-nav-link next">
            <span class="help-page-nav-label">Next</span>
            <span class="help-page-nav-title">{{ nextPage.text }}</span>
          </router-link>
        </div>
      </div>

      <!-- Table of Contents (Right Sidebar) -->
      <aside class="help-toc" v-if="tocItems.length > 0">
        <div class="help-toc-title">On this page</div>
        <ul class="help-toc-list">
          <li v-for="item in tocItems" :key="item.id" :class="'toc-level-' + item.level">
            <a :href="'#' + item.id" class="help-toc-link" :class="{ active: activeHeading === item.id }">{{ item.text }}</a>
          </li>
        </ul>
      </aside>
    </div>
  </div>

 </div>
 </MarketingShell>
</template>

<script>
import MarketingShell from "./marketing/MarketingShell.vue";
import { Marked } from 'marked'
import { docs, navigation, buildSearchIndex } from '../docs/index.js'

export default {
  components: { MarketingShell },
  name: 'Help',
  data() {
    return {

      sidebarOpen: false,
      searchQuery: '',
      searchFocused: false,
      searchResults: [],
      searchIndex: [],
      highlightedIndex: -1,
      navigation: navigation,
      currentSlug: 'introduction',
      renderedContent: '',
      tocItems: [],
      activeHeading: '',
      scrollObserver: null,
      // Profile menu state
      profileMenuOpen: false,
      // Maintenance banner
      showMaintenanceBanner: false,
    }
  },
  computed: {
    isDark() { return this.$store.getters.isDarkTheme },
    flatNav() {
      const items = []
      for (const group of this.navigation) {
        for (const item of group.items) {
          items.push(item)
        }
      }
      return items
    },
    currentIndex() {
      return this.flatNav.findIndex(item => item.link === this.currentSlug)
    },
    prevPage() {
      return this.currentIndex > 0 ? this.flatNav[this.currentIndex - 1] : null
    },
    nextPage() {
      return this.currentIndex < this.flatNav.length - 1 ? this.flatNav[this.currentIndex + 1] : null
    }
  },
  watch: {
    '$route'() {
      this.loadCurrentPage()
      this.sidebarOpen = false
    }
  },
  mounted() {
    this.searchIndex = buildSearchIndex()
    this.loadCurrentPage()
    this.setupKeyboardShortcuts()
    this.setupScrollSpy()
    // Add click outside listener for dropdown
    document.addEventListener('click', this.handleClickOutside)
  },
  beforeDestroy() {
    if (this.scrollObserver) {
      this.scrollObserver.disconnect()
    }
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('click', this.handleClickOutside)
  },
  methods: {
    // Profile menu methods
    toggleProfileMenu() {
      this.profileMenuOpen = !this.profileMenuOpen;
    },
    handleClickOutside(event) {
      const dropdown = this.$el.querySelector('.user-profile-dropdown');
      if (dropdown && !dropdown.contains(event.target)) {
        this.profileMenuOpen = false;
      }
    },
    handleMenuClick(action) {
      this.profileMenuOpen = false;
      switch (action) {
        case 'dashboard':
          this.$router.push('/dashboard');
          break;
        case 'pricing':
          this.$router.push('/pricing');
          break;
        case 'chart':
          this.$router.push('/chart');
          break;
        case 'help':
          // Already on help page
          break;
        case 'download':
          this.$router.push('/download');
          break;
        case 'logout':
          this.handleLogout();
          break;
      }
    },
    getUserInitials() {
      if (this.$clerk.user && this.$clerk.user.firstName && this.$clerk.user.lastName) {
        return (this.$clerk.user.firstName[0] + this.$clerk.user.lastName[0]).toUpperCase();
      }
      if (this.$clerk.user && this.$clerk.user.firstName) {
        return this.$clerk.user.firstName[0].toUpperCase();
      }
      if (this.$clerk.user && this.$clerk.user.email) {
        return this.$clerk.user.email[0].toUpperCase();
      }
      return 'U';
    },
    loadCurrentPage() {
      const slug = this.$route.params.slug || 'introduction'
      this.currentSlug = slug

      const markdown = docs[slug]
      if (!markdown) {
        this.renderedContent = ''
        this.tocItems = []
        return
      }

      // Strip frontmatter
      const content = markdown.replace(/^---[\s\S]*?---\s*/, '')

      // Configure marked for proper rendering
      const tocItems = []

      const instance = new Marked({
        gfm: true,
        renderer: {
          heading({ tokens, depth }) {
            const text = tokens.map(t => t.raw || t.text || '').join('')
            const id = text.toLowerCase()
              .replace(/[^\w\s-]/g, '')
              .replace(/\s+/g, '-')
              .replace(/-+/g, '-')
              .trim()

            if (depth === 2 || depth === 3) {
              tocItems.push({ id, text, level: depth })
            }

            return `<h${depth} id="${id}">${this.parser.parseInline(tokens)}</h${depth}>\n`
          }
        }
      })

      this.renderedContent = instance.parse(content)
      this.tocItems = tocItems

      // Scroll to top
      this.$nextTick(() => {
        const main = this.$el.querySelector('.help-main')
        if (main) main.scrollTop = 0
        this.setupScrollSpy()
      })
    },
    setupScrollSpy() {
      if (this.scrollObserver) {
        this.scrollObserver.disconnect()
      }

      this.$nextTick(() => {
        const headings = this.$el.querySelectorAll('.help-content h2, .help-content h3')
        if (!headings.length) return

        this.scrollObserver = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                this.activeHeading = entry.target.id
                break
              }
            }
          },
          { rootMargin: '-80px 0px -80% 0px', threshold: 0 }
        )

        headings.forEach(h => this.scrollObserver.observe(h))
      })
    },
    setupKeyboardShortcuts() {
      this.handleKeydown = (e) => {
        if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
          e.preventDefault()
          this.$refs.searchInput.focus()
        }
      }
      document.addEventListener('keydown', this.handleKeydown)
    },
    performSearch() {
      if (!this.searchQuery.trim()) {
        this.searchResults = []
        this.highlightedIndex = -1
        return
      }

      const query = this.searchQuery.toLowerCase()
      this.searchResults = this.searchIndex
        .filter(item => {
          return item.title.toLowerCase().includes(query) ||
                 item.description.toLowerCase().includes(query) ||
                 item.content.toLowerCase().includes(query)
        })
        .slice(0, 8)
      this.highlightedIndex = -1
    },
    clearSearch() {
      this.searchQuery = ''
      this.searchResults = []
      this.highlightedIndex = -1
      this.$refs.searchInput.blur()
    },
    onSearchBlur() {
      setTimeout(() => {
        this.searchFocused = false
      }, 200)
    },
    highlightNext() {
      if (this.highlightedIndex < this.searchResults.length - 1) {
        this.highlightedIndex++
      }
    },
    highlightPrev() {
      if (this.highlightedIndex > 0) {
        this.highlightedIndex--
      }
    },
    selectHighlighted() {
      if (this.highlightedIndex >= 0 && this.searchResults[this.highlightedIndex]) {
        this.navigateTo(this.searchResults[this.highlightedIndex].slug)
      }
    },
    navigateTo(slug) {
      this.$router.push('/help/' + slug)
      this.clearSearch()
    }
  }
}
</script>

<style scoped>
/* ===== CSS Variables ===== */
.help-page {
  --help-bg: #ffffff;
  --help-bg-soft: #f9fafb;
  --help-bg-mute: #f3f4f6;
  --help-border: #e5e7eb;
  --help-text: #1f2937;
  --help-text-2: #4b5563;
  --help-text-3: #9ca3af;
  --help-accent: #6366f1;
  --help-accent-soft: rgba(99, 102, 241, 0.08);
  --help-accent-hover: #4f46e5;
  --help-header-bg: rgba(255, 255, 255, 0.85);
  --help-sidebar-bg: #f9fafb;
  --help-code-bg: #f3f4f6;
  --help-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
  --help-radius: 8px;
}

.help-page.dark-mode {
  --help-bg: #0f172a;
  --help-bg-soft: #1e293b;
  --help-bg-mute: #334155;
  --help-border: #334155;
  --help-text: #f1f5f9;
  --help-text-2: #cbd5e1;
  --help-text-3: #64748b;
  --help-accent: #818cf8;
  --help-accent-soft: rgba(129, 140, 248, 0.1);
  --help-accent-hover: #a5b4fc;
  --help-header-bg: rgba(15, 23, 42, 0.85);
  --help-sidebar-bg: #1e293b;
  --help-code-bg: #1e293b;
  --help-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
}

/* ===== Reset & Base ===== */
.help-page {
  min-height: 100vh;
  background: var(--help-bg);
  color: var(--help-text);
  font-family: -apple-system, BlinkMacSystemFont, 'Inter', 'Segoe UI', Roboto, sans-serif;
  line-height: 1.6;
  -webkit-font-smoothing: antialiased;
}

/* ===== Header ===== */
.help-header {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  height: 60px;
  background: var(--help-header-bg);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid var(--help-border);
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 24px;
}

.help-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.mobile-menu-btn {
  display: none;
  background: none;
  border: none;
  color: var(--help-text);
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
}

.mobile-menu-btn:hover {
  background: var(--help-bg-mute);
}

.help-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  text-decoration: none;
  color: var(--help-text);
}

.help-brand-logo {
  height: 28px;
  width: auto;
  border-radius: 4px;
}

.help-brand-text {
  font-weight: 600;
  font-size: 15px;
}

.help-brand-divider {
  color: var(--help-text-3);
  font-weight: 300;
  font-size: 20px;
}

.help-brand-section {
  color: var(--help-text-2);
  font-size: 15px;
  font-weight: 500;
}

.help-header-right {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* ===== Search ===== */
.help-search-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  background: var(--help-bg-soft);
  border: 1px solid var(--help-border);
  border-radius: var(--help-radius);
  padding: 0 12px;
  transition: all 0.2s ease;
  width: 240px;
}

.help-search-wrapper.focused {
  border-color: var(--help-accent);
  box-shadow: 0 0 0 3px var(--help-accent-soft);
  width: 320px;
}

.help-search-icon {
  color: var(--help-text-3);
  flex-shrink: 0;
}

.help-search-input {
  border: none;
  background: none;
  outline: none;
  padding: 8px;
  font-size: 14px;
  color: var(--help-text);
  width: 100%;
  font-family: inherit;
}

.help-search-input::placeholder {
  color: var(--help-text-3);
}

.help-search-kbd {
  font-size: 11px;
  padding: 2px 6px;
  border-radius: 4px;
  background: var(--help-bg-mute);
  border: 1px solid var(--help-border);
  color: var(--help-text-3);
  font-family: inherit;
  white-space: nowrap;
}

.help-search-results {
  position: fixed;
  top: 60px;
  right: 60px;
  width: 400px;
  max-height: 480px;
  overflow-y: auto;
  background: var(--help-bg);
  border: 1px solid var(--help-border);
  border-radius: var(--help-radius);
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
  z-index: 200;
}

.help-search-result-item {
  padding: 12px 16px;
  cursor: pointer;
  border-bottom: 1px solid var(--help-border);
  transition: background 0.15s ease;
}

.help-search-result-item:last-child {
  border-bottom: none;
}

.help-search-result-item:hover,
.help-search-result-item.highlighted {
  background: var(--help-accent-soft);
}

.help-search-result-title {
  font-weight: 600;
  font-size: 14px;
  color: var(--help-text);
  margin-bottom: 4px;
}

.help-search-result-desc {
  font-size: 13px;
  color: var(--help-text-3);
  line-height: 1.4;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== Theme Toggle ===== */
.help-theme-btn {
  background: none;
  border: 1px solid var(--help-border);
  border-radius: var(--help-radius);
  padding: 8px;
  cursor: pointer;
  color: var(--help-text-2);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.help-theme-btn:hover {
  background: var(--help-bg-mute);
  color: var(--help-text);
  border-color: var(--help-accent);
}

/* ===== Layout ===== */
.help-layout {
  display: flex;
  padding-top: 60px;
  min-height: 100vh;
}

/* ===== Sidebar ===== */
.help-sidebar {
  position: fixed;
  top: 60px;
  left: 0;
  bottom: 0;
  width: 260px;
  background: var(--help-sidebar-bg);
  border-right: 1px solid var(--help-border);
  overflow-y: auto;
  padding: 20px 0;
  z-index: 50;
}

.help-sidebar::-webkit-scrollbar {
  width: 4px;
}

.help-sidebar::-webkit-scrollbar-thumb {
  background: var(--help-border);
  border-radius: 4px;
}

.help-nav-group {
  margin-bottom: 24px;
  padding: 0 16px;
}

.help-nav-group-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--help-text-3);
  padding: 0 12px;
  margin-bottom: 8px;
}

.help-nav-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.help-nav-list li {
  margin-bottom: 2px;
}

.help-nav-link {
  display: block;
  padding: 6px 12px;
  font-size: 14px;
  color: var(--help-text-2);
  text-decoration: none;
  border-radius: 6px;
  transition: all 0.15s ease;
  line-height: 1.5;
}

.help-nav-link:hover {
  color: var(--help-text);
  background: var(--help-bg-mute);
}

.help-nav-link.active {
  color: var(--help-accent);
  background: var(--help-accent-soft);
  font-weight: 500;
}

.help-nav-link.disabled {
  color: var(--help-text-3);
  cursor: not-allowed;
  opacity: 0.5;
  position: relative;
}

.help-nav-link.disabled:hover {
  color: var(--help-text-3);
  background: transparent;
}

/* ===== Main Content ===== */
.help-main {
  flex: 1;
  margin-left: 260px;
  margin-right: 200px;
  padding: 40px 48px 80px;
  max-width: 820px;
  min-width: 0;
}

/* ===== Content Styles ===== */
.help-content >>> h1 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--help-text);
  margin: 0 0 16px 0;
  line-height: 1.3;
  letter-spacing: -0.02em;
}

.help-content >>> h2 {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--help-text);
  margin: 48px 0 16px 0;
  padding-top: 24px;
  border-top: 1px solid var(--help-border);
  line-height: 1.3;
}

.help-content >>> h2:first-child {
  margin-top: 0;
  padding-top: 0;
  border-top: none;
}

.help-content >>> h3 {
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--help-text);
  margin: 32px 0 12px 0;
  line-height: 1.4;
}

.help-content >>> h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--help-text);
  margin: 24px 0 8px 0;
}

.help-content >>> p {
  font-size: 15px;
  line-height: 1.75;
  color: var(--help-text-2);
  margin: 0 0 16px 0;
}

.help-content >>> a {
  color: var(--help-accent);
  text-decoration: none;
  font-weight: 500;
}

.help-content >>> a:hover {
  color: var(--help-accent-hover);
  text-decoration: underline;
}

.help-content >>> ul,
.help-content >>> ol {
  padding-left: 24px;
  margin: 0 0 16px 0;
}

.help-content >>> li {
  font-size: 15px;
  line-height: 1.75;
  color: var(--help-text-2);
  margin-bottom: 6px;
}

.help-content >>> li strong {
  color: var(--help-text);
}

.help-content >>> code {
  font-family: 'JetBrains Mono', 'Fira Code', 'SF Mono', Menlo, monospace;
  font-size: 13px;
  background: var(--help-code-bg);
  padding: 2px 6px;
  border-radius: 4px;
  color: var(--help-accent);
  border: 1px solid var(--help-border);
}

.help-content >>> pre {
  background: var(--help-code-bg);
  border: 1px solid var(--help-border);
  border-radius: var(--help-radius);
  padding: 16px 20px;
  overflow-x: auto;
  margin: 0 0 20px 0;
  line-height: 1.6;
}

.help-content >>> pre code {
  background: none;
  padding: 0;
  border: none;
  color: var(--help-text);
  font-size: 13px;
}

.help-content >>> blockquote {
  border-left: 3px solid var(--help-accent);
  background: var(--help-accent-soft);
  padding: 12px 16px;
  margin: 0 0 16px 0;
  border-radius: 0 var(--help-radius) var(--help-radius) 0;
}

.help-content >>> blockquote p {
  margin: 0;
  color: var(--help-text);
}

.help-content >>> table {
  width: 100%;
  border-collapse: collapse;
  margin: 0 0 20px 0;
  font-size: 14px;
}

.help-content >>> th {
  background: var(--help-bg-mute);
  padding: 10px 14px;
  text-align: left;
  font-weight: 600;
  color: var(--help-text);
  border: 1px solid var(--help-border);
}

.help-content >>> td {
  padding: 10px 14px;
  border: 1px solid var(--help-border);
  color: var(--help-text-2);
}

.help-content >>> hr {
  border: none;
  border-top: 1px solid var(--help-border);
  margin: 40px 0;
}

.help-content >>> img {
  max-width: 100%;
  border-radius: var(--help-radius);
  border: 1px solid var(--help-border);
}

/* ===== Not Found ===== */
.help-not-found {
  text-align: center;
  padding: 80px 20px;
}

.help-back-link {
  color: var(--help-accent);
  text-decoration: none;
  font-weight: 500;
}

.help-back-link:hover {
  text-decoration: underline;
}

/* ===== Page Navigation ===== */
.help-page-nav {
  display: flex;
  justify-content: space-between;
  margin-top: 64px;
  padding-top: 24px;
  border-top: 1px solid var(--help-border);
  gap: 16px;
}

.help-page-nav-link {
  display: flex;
  flex-direction: column;
  padding: 16px 20px;
  border: 1px solid var(--help-border);
  border-radius: var(--help-radius);
  text-decoration: none;
  transition: all 0.2s ease;
  min-width: 0;
  max-width: 48%;
}

.help-page-nav-link:hover {
  border-color: var(--help-accent);
  background: var(--help-accent-soft);
}

.help-page-nav-link.next {
  text-align: right;
  margin-left: auto;
}

.help-page-nav-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--help-text-3);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 4px;
}

.help-page-nav-title {
  font-size: 15px;
  font-weight: 600;
  color: var(--help-accent);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* ===== Table of Contents ===== */
.help-toc {
  position: fixed;
  top: 80px;
  right: 16px;
  width: 180px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
  padding: 0 8px;
}

.help-toc-title {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--help-text-3);
  margin-bottom: 12px;
}

.help-toc-list {
  list-style: none;
  padding: 0;
  margin: 0;
  border-left: 1px solid var(--help-border);
}

.help-toc-list li {
  margin-bottom: 0;
}

.help-toc-list li.toc-level-3 {
  padding-left: 12px;
}

.help-toc-link {
  display: block;
  padding: 4px 12px;
  font-size: 12px;
  color: var(--help-text-3);
  text-decoration: none;
  line-height: 1.5;
  border-left: 2px solid transparent;
  margin-left: -1px;
  transition: all 0.15s ease;
}

.help-toc-link:hover {
  color: var(--help-text-2);
}

.help-toc-link.active {
  color: var(--help-accent);
  border-left-color: var(--help-accent);
}

/* ════════════════════════════════
   USER PROFILE DROPDOWN
   ════════════════════════════════════ */
.user-profile-dropdown {
  position: relative;
  margin-left: 8px;
}

.user-profile-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 6px 12px;
  border: 1.5px solid var(--help-border);
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.15s;
}

.dark-mode .user-profile-btn {
  border-color: var(--help-border);
}

.user-profile-btn:hover {
  border-color: var(--help-accent);
  background: rgba(34, 197, 94, 0.05);
}

.dark-mode .user-profile-btn:hover {
  border-color: var(--help-accent);
  background: rgba(34, 197, 94, 0.1);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--help-bg-2);
}

.dark-mode .user-avatar {
  background: var(--help-bg-1);
}

.user-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  color: var(--help-text-3);
}

.dark-mode .avatar-placeholder {
  color: var(--help-text-2);
}

.user-name {
  font-size: 14px;
  font-weight: 500;
  color: var(--help-text-1);
  max-width: 120px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dark-mode .user-name {
  color: var(--help-text-2);
}

.dropdown-arrow {
  color: var(--help-text-3);
  transition: transform 0.2s;
}

.dark-mode .dropdown-arrow {
  color: var(--help-text-2);
}

.user-profile-dropdown.active .dropdown-arrow {
  transform: rotate(180deg);
}

.profile-dropdown-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  min-width: 200px;
  background: var(--help-bg-1);
  border: 1px solid var(--help-border);
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  opacity: 0;
  visibility: hidden;
  transform: translateY(-8px);
  transition: all 0.2s;
  z-index: 1000;
}

.dark-mode .profile-dropdown-menu {
  background: var(--help-bg-2);
  border-color: var(--help-border);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.user-profile-dropdown.active .profile-dropdown-menu {
  opacity: 1;
  visibility: visible;
  transform: translateY(0);
}

.menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  color: var(--help-text-1);
  text-decoration: none;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
}

.dark-mode .menu-item {
  color: var(--help-text-2);
}

.menu-item:hover {
  background: var(--help-bg-2);
  color: var(--help-accent);
}

.dark-mode .menu-item:hover {
  background: rgba(34, 197, 94, 0.1);
  color: var(--help-accent);
}

.menu-item:first-child {
  border-radius: 8px 8px 0 0;
}

.menu-item:last-child {
  border-radius: 0 0 8px 8px;
}

.menu-divider {
  height: 1px;
  background: var(--help-border);
  margin: 4px 0;
}

.logout-item {
  color: var(--help-error);
}

.dark-mode .logout-item {
  color: var(--help-error);
}

.logout-item:hover {
  background: rgba(220, 38, 38, 0.1);
  color: var(--help-error);
}

.dark-mode .logout-item:hover {
  background: rgba(239, 68, 68, 0.1);
  color: var(--help-error);
}

/* ===== Sidebar Overlay ===== */
.help-sidebar-overlay {
  display: none;
}

/* ===== Responsive ===== */
@media (max-width: 1200px) {
  .help-toc {
    display: none;
  }
  .help-main {
    margin-right: 0;
  }
}

@media (max-width: 768px) {
  .mobile-menu-btn {
    display: flex;
  }

  .help-brand-text,
  .help-brand-divider {
    display: none;
  }

  .help-search-wrapper {
    width: 160px;
  }

  .help-search-wrapper.focused {
    width: 200px;
  }

  .help-search-kbd {
    display: none;
  }

  .help-search-results {
    left: 16px;
    right: 16px;
    width: auto;
  }

  .help-sidebar {
    transform: translateX(-100%);
    transition: transform 0.25s ease;
    z-index: 90;
    background: var(--help-bg);
  }

  .help-sidebar.open {
    transform: translateX(0);
  }

  .help-sidebar-overlay {
    display: block;
    position: fixed;
    inset: 0;
    top: 60px;
    background: rgba(0, 0, 0, 0.4);
    z-index: 80;
  }

  .help-main {
    margin-left: 0;
    padding: 24px 20px 60px;
  }

  .help-content >>> h1 {
    font-size: 1.6rem;
  }

  .help-content >>> h2 {
    font-size: 1.3rem;
  }

  .help-page-nav {
    flex-direction: column;
  }

  .help-page-nav-link {
    max-width: 100%;
  }

  .help-page-nav-link.next {
    text-align: left;
  }
}

/* ===== Maintenance Banner ===== */
.maintenance-banner {
  background: linear-gradient(135deg, #fbbf24, #f59e0b);
  color: #78350f;
  border-bottom: 2px solid #d97706;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(217, 119, 6, 0.3);
}

.dark-mode .maintenance-banner {
  background: linear-gradient(135deg, #d97706, #b45309);
  color: #fef3c7;
  border-bottom-color: #92400e;
}

.maintenance-content {
  display: flex;
  align-items: center;
  padding: 12px 24px;
  gap: 16px;
  max-width: 1200px;
  margin: 0 auto;
}

.maintenance-icon {
  flex-shrink: 0;
  animation: spin 2s linear infinite;
}

@keyframes spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.maintenance-text {
  flex: 1;
}

.maintenance-text h3 {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
}

.maintenance-text p {
  margin: 0;
  font-size: 13px;
  opacity: 0.9;
  line-height: 1.4;
}

.maintenance-close {
  background: rgba(255, 255, 255, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 6px;
  padding: 6px;
  cursor: pointer;
  color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.maintenance-close:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

/* ===== Maintenance Overlay ===== */
.maintenance-overlay {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  text-align: center;
  padding: 40px 20px;
}

.maintenance-overlay-content {
  max-width: 500px;
  animation: fadeInUp 0.6s ease;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.maintenance-overlay-icon {
  margin-bottom: 24px;
  color: var(--help-accent);
  animation: pulse 2s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

.maintenance-overlay-content h2 {
  font-size: 2rem;
  font-weight: 700;
  color: var(--help-text);
  margin: 0 0 16px 0;
  line-height: 1.3;
}

.maintenance-overlay-content p {
  font-size: 16px;
  line-height: 1.6;
  color: var(--help-text-2);
  margin: 0 0 16px 0;
}

.maintenance-eta {
  font-size: 14px;
  color: var(--help-text-3);
  font-style: italic;
}

.maintenance-actions {
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-top: 32px;
  flex-wrap: wrap;
}

.maintenance-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
  min-width: 120px;
}

.maintenance-btn.primary {
  background: var(--help-accent);
  color: white;
  border-color: var(--help-accent);
}

.maintenance-btn.primary:hover {
  background: var(--help-accent-hover);
  border-color: var(--help-accent-hover);
  transform: translateY(-1px);
}

.maintenance-btn.secondary {
  background: transparent;
  color: var(--help-accent);
  border-color: var(--help-accent);
}

.maintenance-btn.secondary:hover {
  background: var(--help-accent-soft);
  transform: translateY(-1px);
}

/* Adjust header position when banner is visible */
.help-page:has(.maintenance-banner) .help-header {
  top: 60px;
}

.help-page:has(.maintenance-banner) .help-layout {
  padding-top: 120px;
}

.help-page:has(.maintenance-banner) .help-sidebar {
  top: 120px;
}

.help-page:has(.maintenance-banner) .help-toc {
  top: 140px;
}
</style>
